// src/pages/Service/ServiceReturn/ServiceReturnVoucher.js
// Layout follows ServiceVoucher (Service/Service/ServiceVoucher).
import React, { useEffect, useRef, useState } from "react";
import { Button, Card, Grid, Space, Spin, Switch, Typography } from "antd";
import { ArrowLeftOutlined, PrinterOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";

import SearchListAutoComplete from "../../../components/SearchListAutoComplete";
import ReportDom from "../../../components/common/ReportDom";
import { getInstitutionApi } from "../../../apiservice/setting";
import { getServiceReturnDetailsApi, getServiceReturnVouchersApi } from "../../../apiservice/service";
import ServiceReturnVoucherBody from "./ServiceReturnVoucherBody";

const { Text } = Typography;

const ServiceReturnVoucher = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const reportRef = useRef(null);
  const screens = Grid.useBreakpoint();

  const [vouchers, setVouchers] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [institution, setInstitution] = useState(null);
  const [showStamp, setShowStamp] = useState(false);
  const [showSignature, setShowSignature] = useState(false);

  const loadReturn = (service_r_id) => {
    if (!service_r_id) {
      setData(null);
      return;
    }
    setLoading(true);
    getServiceReturnDetailsApi({ service_r_id })
      .then((res) => setData(res.data || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    getServiceReturnVouchersApi({})
      .then((res) => setVouchers(res.data || []))
      .catch(() => setVouchers([]));
    getInstitutionApi()
      .then((res) => setInstitution(res.data))
      .catch(() => setInstitution(null));
    loadReturn(new URLSearchParams(location.search).get("id"));
  }, []);

  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: `Service Return-${data?.service_r_voucher_no || ""}`,
    removeAfterPrint: true,
  });

  return (
    <div className="p-4 voucher-wrapper">
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          gap: 8,
        }}
      >
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          {!screens.xs && "Back"}
        </Button>
        <div style={{ flex: "1 1 200px", minWidth: 0, maxWidth: screens.xs ? "100%" : 400, order: screens.xs ? 3 : 0 }}>
          <SearchListAutoComplete
            entityType="voucher"
            placeholder="Voucher No"
            optionsList={vouchers.map((v) => ({ sale_id: v.service_r_id, display_text: v.display_text, raw: v }))}
            value={data ? { display_text: data.service_r_voucher_no } : null}
            onSelect={(obj) => loadReturn(obj?.raw?.service_r_id)}
            showAddNew={false}
          />
        </div>
        <Space align="center" wrap style={{ order: screens.xs ? 2 : 0 }}>
          <Switch checked={showStamp} onChange={setShowStamp} checkedChildren="Stamp" unCheckedChildren="Stamp" />
          <Switch
            checked={showSignature}
            onChange={setShowSignature}
            checkedChildren="Signature"
            unCheckedChildren="Signature"
          />
          <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint} disabled={!data}>
            {!screens.xs && "Print"}
          </Button>
        </Space>
      </div>

      <Card className="shadow-md rounded-xl">
        {!data ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            {loading ? <Spin /> : <Text type="secondary">Search and select a voucher</Text>}
          </div>
        ) : (
          <ServiceReturnVoucherBody
            data={data}
            institution={institution}
            showStamp={showStamp}
            showSignature={showSignature}
          />
        )}
      </Card>

      {data && (
        <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
          <ReportDom
            ref={reportRef}
            mode={showSignature || showStamp ? "signatureOn" : ""}
            renderTable={() => (
              <ServiceReturnVoucherBody
                data={data}
                institution={institution}
                showStamp={showStamp}
                showSignature={showSignature}
              />
            )}
          />
        </div>
      )}
    </div>
  );
};

export default ServiceReturnVoucher;
